// src/main.js
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { initializeKeycloak } from './keycloak'
import keycloak from './keycloak'
import axios from 'axios'
import VueMatomo from 'vue-matomo'
import i18n from './i18n'
import "./assets/app-font.css";

// --- Matomo settings ---
const MATOMO_URL = 'https://analytics.servogpt.com/'
const MATOMO_SITE_ID = 1
const MATOMO_TRACKER_FILE = 'matomo'

const MATOMO_OPTIONS = {
  router,
  siteId: MATOMO_SITE_ID,
  host: MATOMO_URL,
  trackerFileName: MATOMO_TRACKER_FILE,
  enableLinkTracking: true,
  trackInitialView: true,
  requireConsent: true, // Changed to require consent for GDPR compliance
  disableCookies: false,
  debug: false
}

function setAuthHeader() {
  if (keycloak && keycloak.token) {
    axios.defaults.headers.common.Authorization = `Bearer ${keycloak.token}`
  } else {
    delete axios.defaults.headers.common.Authorization
  }
}

function mountAppWithMatomo(preAuthUid) {
  // Prime Matomo (before plugin) if we already have a user id
  if (preAuthUid) {
    window._paq = window._paq || []
    window._paq.push(['setUserId', preAuthUid])
  }

  const app = createApp(App)
  app.use(router)
  app.use(i18n)
  app.use(VueMatomo, MATOMO_OPTIONS)

  // Also set via Matomo API after plugin install
  const matomo = app.config.globalProperties.$matomo
  if (matomo && preAuthUid && typeof matomo.setUserId === 'function') {
    matomo.setUserId(preAuthUid)
  }

  // Token lifecycle — stay in guest mode if refresh fails
  keycloak.onTokenExpired = async () => {
    try {
      const refreshed = await keycloak.updateToken(30)
      if (refreshed && keycloak.token) {
        setAuthHeader()
        const newUid = keycloak.tokenParsed?.sub
        if (newUid && matomo && typeof matomo.setUserId === 'function') {
          matomo.setUserId(newUid)
        }
      } else {
        // Treat as guest
        setAuthHeader() // clears header
        if (matomo && typeof matomo.resetUserId === 'function') {
          matomo.resetUserId()
        }
      }
    } catch (e) {
      // Refresh failed — remain guest
      setAuthHeader() // clears header
      if (matomo && typeof matomo.resetUserId === 'function') {
        matomo.resetUserId()
      }
      // No keycloak.login()/logout() here
    }
  }

  keycloak.onAuthSuccess = () => {
    const id = keycloak.tokenParsed?.sub
    if (id) {
      window._paq = window._paq || []
      window._paq.push(['setUserId', id])
      if (matomo && typeof matomo.setUserId === 'function') {
        matomo.setUserId(id)
      }
    }
    setAuthHeader()
  }

  keycloak.onAuthLogout = () => {
    setAuthHeader() // clears header
    window._paq = window._paq || []
    window._paq.push(['resetUserId'])
    if (matomo && typeof matomo.resetUserId === 'function') {
      matomo.resetUserId()
    }
  }

  app.mount('#app')
}

initializeKeycloak()
  .then(() => {
    // Auth (or guest) ready — set header if we have a token
    setAuthHeader()

    // Grab uid if authenticated
    const uid = keycloak.tokenParsed?.sub
    mountAppWithMatomo(uid)
  })
  .catch(err => {
    // If initializeKeycloak() rejects when unauthenticated, still mount as guest
    console.warn('Keycloak init (guest path):', err)
    setAuthHeader() // clears header
    mountAppWithMatomo(null)
  })
